import React, { useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import PathfinderSelectBar from './PathfinderSelectBar';
import { applyTypography, BaseButton } from '@repo/common';

const DEFAULT_OPTION_COUNT = 7;
const DEFAULT_OPTION_TITLE = 'Option';
const DEFAULT_OPTION_CAPTION = 'caption';

const rowClassName =
  'flex w-full flex-col items-stretch gap-4 tabletH:flex-row tabletH:items-center';
const tallRowClassName =
  'flex w-full flex-col items-stretch gap-4 tabletH:flex-row';
const fillCardClassName = 'min-h-px min-w-0 flex-1 max-w-none';
const ROLE_LABEL_OVERRIDES = {
  ROLE_SALES: 'Sales&CS',
  ROLE_etc: 'etc',
};

const layoutPresets = {
  two: {
    slotCount: 2,
    columns: 2,
    rowClassName: tallRowClassName,
    cardProps: { size: 'large' },
    cardClassName: 'h-[200px] self-stretch justify-center gap-4',
  },
  four: {
    slotCount: 4,
    columns: 2,
    rowClassName,
    cardProps: { size: 'large' },
    cardClassName: '',
  },
  six: {
    slotCount: 6,
    columns: 2,
    rowClassName,
    cardProps: { size: 'small', iconPosition: 'left' },
    cardClassName: '',
  },
  nine: {
    slotCount: 9,
    columns: 3,
    rowClassName,
    cardProps: { size: 'small', iconPosition: 'none' },
    cardClassName: '',
  },
};

const chunk = (items, size) => {
  const rows = [];

  for (let index = 0; index < items.length; index += size) {
    rows.push(items.slice(index, index + size));
  }

  return rows;
};

const createFallbackOptions = (count) =>
  Array.from({ length: Math.max(0, count) }, (_, index) => ({
    id: `option-${index + 1}`,
    title: DEFAULT_OPTION_TITLE,
    caption: DEFAULT_OPTION_CAPTION,
  }));

const normalizeOption = (option, index) => {
  if (typeof option === 'string') {
    return {
      id: `option-${index + 1}`,
      title: option,
      caption: DEFAULT_OPTION_CAPTION,
    };
  }

  return {
    id: option?.id ?? `option-${index + 1}`,
    title: option?.title ?? DEFAULT_OPTION_TITLE,
    caption: option?.caption ?? DEFAULT_OPTION_CAPTION,
    icon: option?.icon,
    selectedIcon: option?.selectedIcon,
  };
};

const resolveRoleTitle = (option) =>
  ROLE_LABEL_OVERRIDES[option?.id] ?? option?.title ?? DEFAULT_OPTION_TITLE;

const resolveLayoutPreset = (count) => {
  if (count <= 2) return layoutPresets.two;
  if (count <= 4) return layoutPresets.four;
  if (count <= 6) return layoutPresets.six;

  return layoutPresets.nine;
};

/**
 * Reusable option grid for Pathfinder questions.
 *
 * - 1-2 options: 2-card layout
 * - 3-4 options: 4-card layout with empty slots
 * - 5-6 options: 6-card layout with empty slots
 * - 7+ options: 9-card style grid with empty slots up to 9
 */
function SelectOption({
  options,
  optionCount = DEFAULT_OPTION_COUNT,
  value,
  defaultValue,
  onChange,
  layoutVariant = 'default',
  className = '',
}) {
  const normalizedOptions = useMemo(() => {
    const sourceOptions = Array.isArray(options)
      ? options
      : createFallbackOptions(optionCount);

    return sourceOptions.map(normalizeOption);
  }, [optionCount, options]);

  const layoutPreset = useMemo(
    () => resolveLayoutPreset(normalizedOptions.length || optionCount),
    [normalizedOptions.length, optionCount]
  );

  const slotCount =
    layoutPreset === layoutPresets.nine
      ? Math.max(layoutPreset.slotCount, normalizedOptions.length)
      : layoutPreset.slotCount;

  const optionSlots = useMemo(() => {
    const slots = [...normalizedOptions];

    while (slots.length < slotCount) {
      slots.push(null);
    }

    return slots;
  }, [normalizedOptions, slotCount]);

  const rows = useMemo(
    () => chunk(optionSlots, layoutPreset.columns),
    [optionSlots, layoutPreset.columns]
  );

  const [internalValue, setInternalValue] = useState(
    defaultValue ?? normalizedOptions[0]?.id ?? null
  );

  const selectedValue = value !== undefined ? value : internalValue;

  useEffect(() => {
    if (value !== undefined) return;

    const hasSelectedOption = normalizedOptions.some(
      (option) => option.id === internalValue
    );

    if (!hasSelectedOption) {
      setInternalValue(defaultValue ?? normalizedOptions[0]?.id ?? null);
    }
  }, [defaultValue, internalValue, normalizedOptions, value]);

  const handleSelect = (option) => {
    if (!option) return;

    if (value === undefined) {
      setInternalValue(option.id);
    }

    onChange?.(option.id, option);
  };

  if (layoutVariant === 'role-grid') {
    return (
      <div
        className={twMerge(
          'grid w-full grid-cols-1 gap-4 self-stretch tabletH:grid-cols-2 tabletV:grid-cols-3',
          className
        )}
        role='radiogroup'
        aria-label='Pathfinder options'
      >
        {normalizedOptions.map((option) => {
          const isCurrentOptionSelected = selectedValue === option.id;
          const roleTitle = resolveRoleTitle(option);

          return (
            <BaseButton
              key={option.id}
              type='text'
              size='m'
              htmlType='button'
              hideIcon
              typo={false}
              isStatic
              role='radio'
              aria-checked={isCurrentOptionSelected}
              className={twMerge(
                'h-auto min-w-0 cursor-pointer min-h-[56px] items-center justify-center rounded-[15px] border-2 px-6 py-4 text-center transition-colors duration-200',
                isCurrentOptionSelected
                  ? 'border-surface-green40 bg-green-light01 text-green-deep02'
                  : 'border-transparent bg-surface-lightgray60 text-text-secondary hover:border-surface-green40 hover:bg-green-light01 hover:text-green-deep02'
              )}
              textWrapperClass={twMerge(
                'h-auto w-full',
                applyTypography('b16Strong', roleTitle)
              )}
              onClick={() => handleSelect(option)}
            >
              {roleTitle}
            </BaseButton>
          );
        })}
      </div>
    );
  }

  if (layoutVariant === 'visa-grid') {
    const visaGridCols = normalizedOptions.length <= 3
      ? 'grid-cols-3'
      : 'grid-cols-1 tabletH:grid-cols-2 tabletV:grid-cols-3';
    return (
      <div
        className={twMerge(
          'grid w-full gap-4 self-stretch',
          visaGridCols,
          className
        )}
        role='radiogroup'
        aria-label='Pathfinder options'
      >
        {normalizedOptions.map((option) => {
          const isCurrentOptionSelected = selectedValue === option.id;

          return (
            <BaseButton
              key={option.id}
              type='text'
              size='m'
              htmlType='button'
              hideIcon
              typo={false}
              isStatic
              role='radio'
              aria-checked={isCurrentOptionSelected}
              className={twMerge(
                'h-auto min-w-0 cursor-pointer min-h-[76px] flex-col items-center justify-center rounded-[15px] border-2 px-6 py-4 text-center transition-colors duration-200',
                isCurrentOptionSelected
                  ? 'border-surface-green40 bg-green-light01'
                  : 'border-transparent bg-surface-lightgray60 hover:border-surface-green40 hover:bg-green-light01'
              )}
              textWrapperClass='flex h-auto w-full flex-col items-center justify-center'
              onClick={() => handleSelect(option)}
            >
              <span className={twMerge('text-base font-medium', isCurrentOptionSelected ? 'text-green-deep02' : 'text-text-secondary')}>
                {option.title}
              </span>
              {option.caption && option.caption !== DEFAULT_OPTION_CAPTION && (
                <span className={twMerge('text-[13px] font-normal', isCurrentOptionSelected ? 'text-green-deep02' : 'text-text-placeholder')}>
                  {option.caption}
                </span>
              )}
            </BaseButton>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        'flex w-full flex-col items-start self-stretch gap-4',
        className
      )}
      role='radiogroup'
      aria-label='Pathfinder options'
    >
      {rows.map((row, rowIndex) => (
        <div
          key={`option-row-${rowIndex}`}
          className={layoutPreset.rowClassName}
        >
          {row.map((option, columnIndex) => {
            const cardClassName = twMerge(
              fillCardClassName,
              layoutPreset.cardClassName
            );

            if (!option) {
              return (
                <PathfinderSelectBar
                  key={`placeholder-${rowIndex}-${columnIndex}`}
                  {...layoutPreset.cardProps}
                  title=' '
                  caption=' '
                  placeholder
                  aria-hidden='true'
                  tabIndex={-1}
                  className={twMerge(cardClassName, 'hidden tabletH:flex')}
                />
              );
            }

            const isCurrentOptionSelected = selectedValue === option.id;

            return (
              <PathfinderSelectBar
                key={option.id}
                {...layoutPreset.cardProps}
                {...(option.icon ? { icon: option.icon } : {})}
                {...(option.selectedIcon
                  ? { selectedIcon: option.selectedIcon }
                  : {})}
                title={option.title}
                caption={option.caption}
                state={isCurrentOptionSelected ? 'selected' : 'default'}
                className={cardClassName}
                onClick={() => handleSelect(option)}
                aria-pressed={isCurrentOptionSelected}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default SelectOption;
